import { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Download,
  FileSpreadsheet,
  Calendar,
  Printer,
  Filter,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  BarChart3,
  AlertTriangle,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  server_post_data,
  get_kitchen_indent_report,
  get_department_with_categories,
} from "../ServiceConnection/serviceconnection";
import { retrieveData } from "../LocalConnection/LocalConnection.js";
import * as XLSX from "xlsx";
import Loader from "../components/layout/Loader";

const statusConfig = {
  0: {
    label: "Pending",
    color: "bg-warning/20 text-warning border-warning/30",
    icon: Clock,
    value: "pending",
  },
  1: {
    label: "Approved",
    color: "bg-success/20 text-success border-success/30",
    icon: CheckCircle2,
    value: "approved",
  },
  2: {
    label: "Converted to PO",
    color: "bg-primary/20 text-primary border-primary/30",
    icon: Eye,
    value: "converted",
  },
  3: {
    label: "Rejected",
    color: "bg-destructive/20 text-destructive border-destructive/30",
    icon: XCircle,
    value: "rejected",
  },
};

const priorityConfig = {
  high: { label: "High", color: "bg-destructive/20 text-destructive" },
  medium: { label: "Medium", color: "bg-warning/20 text-warning" },
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
};

export default function IndentsReports() {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportStats, setReportStats] = useState({});
  const [departments, setDepartments] = useState([]);

  // Filter States
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Restaurant Info
  const restaurantName = retrieveData("restaurant_name") || "UpRestro";
  const restaurantAddress = retrieveData("restaurant_address") || "";

  // Fetch Departments
  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");

    try {
      const response = await server_post_data(
        get_department_with_categories,
        fd
      );
      if (!response.data.error && response.data.message?.data) {
        setDepartments(response.data.message.data);
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, []);

  // Fetch Report Data with Backend Filtering
  const fetchReportData = useCallback(async () => {
    setLoading(true);
    const fd = new FormData();
    
    fd.append("status", selectedStatus === "all" ? "" : selectedStatus);
    fd.append("department", selectedDepartment === "all" ? "" : selectedDepartment);
    fd.append("priority", selectedPriority === "all" ? "" : selectedPriority);
    
    // Add date filters if custom range is selected
    if (fromDate && toDate) {
      fd.append("start_date", fromDate);
      fd.append("end_date", toDate);
    }

    try {
      const response = await server_post_data(get_kitchen_indent_report, fd);
      if (!response.data.error) {
        setReportData(response.data.message.report_list || []);
        setReportStats(response.data.message.summary || {});
      } else {
        setReportData([]);
        setReportStats({});
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      setReportData([]);
      setReportStats({});
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment, selectedStatus, selectedPriority, fromDate, toDate]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    if (departments.length > 0) {
      fetchReportData();
    }
  }, [departments, fetchReportData]);

  // Date Filter Handler
  const handleQuickDateFilter = (filter) => {
    setDateFilter(filter);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case "today":
        setFromDate(today.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        setFromDate(yesterday.toISOString().split("T")[0]);
        setToDate(yesterday.toISOString().split("T")[0]);
        break;
      case "week":
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        setFromDate(weekAgo.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "month":
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        setFromDate(monthAgo.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        break;
      case "all":
        setFromDate("");
        setToDate("");
        break;
      default:
        break;
    }
  };

  // Export to Excel with Professional Formatting
  const exportToExcel = () => {
    const departmentName =
      selectedDepartment === "all"
        ? "All Departments"
        : selectedDepartment;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([[]]);

    let currentRow = 0;

    // Title
    XLSX.utils.sheet_add_aoa(ws, [["INDENTS REPORT"]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"] = [
      { s: { r: currentRow, c: 0 }, e: { r: currentRow, c: 11 } },
    ];
    currentRow += 1;

    // Restaurant Name
    XLSX.utils.sheet_add_aoa(ws, [[restaurantName]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 11 },
    });
    currentRow += 1;

    // Restaurant Address
    if (restaurantAddress) {
      XLSX.utils.sheet_add_aoa(ws, [[restaurantAddress]], {
        origin: { r: currentRow, c: 0 },
      });
      ws["!merges"].push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow, c: 11 },
      });
      currentRow += 1;
    }

    currentRow += 1;

    // Report metadata
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`Department: ${departmentName}`],
        [`Status: ${selectedStatus === "all" ? "All Status" : selectedStatus}`],
        [
          `Priority: ${
            selectedPriority === "all" ? "All Priorities" : selectedPriority
          }`,
        ],
        [
          `Report Date: ${new Date().toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}`,
        ],
        [
          dateFilter !== "all" && fromDate && toDate
            ? `Period: ${new Date(fromDate).toLocaleDateString(
                "en-IN"
              )} to ${new Date(toDate).toLocaleDateString("en-IN")}`
            : "Period: All Time",
        ],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    currentRow += 6;

    // Summary Section
    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], {
      origin: { r: currentRow, c: 0 },
    });
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 1 },
    });
    currentRow += 1;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [`Total Indents: ${reportStats.total_indents || 0}`],
        [`Pending: ${reportStats.pending || 0}`],
        [`Approved: ${reportStats.approved || 0}`],
        [`Converted to PO: ${reportStats.converted || 0}`],
        [`Rejected: ${reportStats.rejected || 0}`],
        [`Total Items Requested: ${reportStats.total_items || 0}`],
        [`High Priority: ${reportStats.high_priority || 0}`],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    currentRow += 8;

    // Table Headers
    const headers = [
      "S.No",
      "Indent No",
      "Department",
      "Item Name",
      "Requested Qty",
      "Approved Qty",
      "Issued Qty",
      "Unit",
      "Stock Available",
      "Need Purchase",
      "Priority",
      "Status",
      "Date",
    ];

    XLSX.utils.sheet_add_aoa(ws, [headers], {
      origin: { r: currentRow, c: 0 },
    });

    const headerRow = currentRow;
    currentRow += 1;

    // Table Data
    const tableData = reportData.map((indent, index) => [
      index + 1,
      indent.indent_no || "",
      indent.department || "",
      indent.ingredient_name || "N/A",
      parseFloat(indent.requested_qty || 0).toFixed(2),
      parseFloat(indent.approved_qty || 0).toFixed(2),
      parseFloat(indent.issued_qty || 0).toFixed(2),
      indent.unit_name || "",
      parseFloat(indent.stock_available || 0).toFixed(2),
      indent.need_purchase ? "Yes" : "No",
      indent.priority || "-",
      statusConfig[indent.status]?.label || "Unknown",
      new Date(indent.date).toLocaleDateString("en-IN"),
    ]);

    XLSX.utils.sheet_add_aoa(ws, tableData, {
      origin: { r: currentRow, c: 0 },
    });
    currentRow += tableData.length;

    // Footer
    currentRow += 1;
    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          `Report Generated: ${new Date().toLocaleString("en-IN", {
            dateStyle: "medium",
            timeStyle: "short",
          })}`,
        ],
      ],
      { origin: { r: currentRow, c: 0 } }
    );
    ws["!merges"].push({
      s: { r: currentRow, c: 0 },
      e: { r: currentRow, c: 5 },
    });

    // Set Column Widths
    ws["!cols"] = [
      { wch: 6 },   // S.No
      { wch: 15 },  // Indent No
      { wch: 20 },  // Department
      { wch: 25 },  // Item Name
      { wch: 12 },  // Requested Qty
      { wch: 12 },  // Approved Qty
      { wch: 12 },  // Issued Qty
      { wch: 10 },  // Unit
      { wch: 12 },  // Stock Available
      { wch: 12 },  // Need Purchase
      { wch: 12 },  // Priority
      { wch: 15 },  // Status
      { wch: 15 },  // Date
    ];

    // Apply Styles
    const titleCell = XLSX.utils.encode_cell({ r: 0, c: 0 });
    if (!ws[titleCell]) ws[titleCell] = { t: "s", v: "" };
    ws[titleCell].s = {
      font: { bold: true, sz: 16, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "0066CC" } },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Restaurant Name
    const nameCell = XLSX.utils.encode_cell({ r: 1, c: 0 });
    if (!ws[nameCell]) ws[nameCell] = { t: "s", v: "" };
    ws[nameCell].s = {
      font: { bold: true, sz: 14 },
      alignment: { horizontal: "center", vertical: "center" },
    };

    // Table Headers Styling
    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: headerRow, c: col });
      if (!ws[cellAddress]) ws[cellAddress] = { t: "s", v: "" };
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 11 },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };
    }

    // Table Data Rows Styling
    for (
      let row = headerRow + 1;
      row < headerRow + 1 + tableData.length;
      row++
    ) {
      const isEvenRow = (row - headerRow) % 2 === 0;
      const indent = reportData[row - headerRow - 1];

      for (let col = 0; col < headers.length; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (!ws[cellAddress]) continue;

        let bgColor = isEvenRow ? "FFFFFF" : "F2F2F2";
        if (indent.status === 3) {
          bgColor = "FFE6E6";
        } else if (indent.status === 0) {
          bgColor = "FFF4E6";
        } else if (indent.status === 1) {
          bgColor = "E6F7E6";
        }

        ws[cellAddress].s = {
          fill: { fgColor: { rgb: bgColor } },
          alignment: {
            horizontal: col === 0 || col >= 4 ? "center" : "left",
            vertical: "center",
          },
          border: {
            top: { style: "thin", color: { rgb: "D0D0D0" } },
            bottom: { style: "thin", color: { rgb: "D0D0D0" } },
            left: { style: "thin", color: { rgb: "D0D0D0" } },
            right: { style: "thin", color: { rgb: "D0D0D0" } },
          },
        };

        if (col === 1 || col === 11) {
          ws[cellAddress].s.font = { bold: true };
        }
      }
    }

    ws["!rows"] = [];
    ws["!rows"][0] = { hpt: 25 };
    ws["!rows"][headerRow] = { hpt: 20 };

    XLSX.utils.book_append_sheet(wb, ws, "Indents Report");

    const date = new Date().toISOString().split("T")[0];
    const filename = `Indents_Report_${departmentName.replace(
      /\s+/g,
      "_"
    )}_${date}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  // Print Report
  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <Header
        title="Indents Reports"
        subtitle="Generate and export detailed indents reports"
      />

      {loading && <Loader />}

      <div className="p-6 space-y-6">
        {/* Filters Card */}
        <Card className="border-primary/20 print:hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Report Filters
                </CardTitle>
                <CardDescription>
                  Configure your report parameters
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handlePrint}
                  variant="outline"
                  className="gap-2"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </Button>
                <Button onClick={exportToExcel} className="gap-2">
                  <Download className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Department, Status, and Priority Filters */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Department</Label>
                <Select
                  value={selectedDepartment}
                  onValueChange={setSelectedDepartment}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.department_id}
                        value={dept.department_name}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={selectedStatus}
                  onValueChange={setSelectedStatus}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="converted">Converted to PO</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={selectedPriority}
                  onValueChange={setSelectedPriority}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date Filters */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Report Period
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={dateFilter === "all" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("all")}
                  >
                    All Time
                  </Button>
                  <Button
                    variant={dateFilter === "today" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("today")}
                  >
                    Today
                  </Button>
                  <Button
                    variant={dateFilter === "yesterday" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("yesterday")}
                  >
                    Yesterday
                  </Button>
                  <Button
                    variant={dateFilter === "week" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("week")}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    variant={dateFilter === "month" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleQuickDateFilter("month")}
                  >
                    Last 30 Days
                  </Button>
                  <Button
                    variant={dateFilter === "custom" ? "default" : "outline"}
                    size="sm"
                    onClick={() => setDateFilter("custom")}
                  >
                    Custom Range
                  </Button>
                </div>
              </div>

              {/* Custom Date Range */}
              {dateFilter === "custom" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg border">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      min={fromDate}
                    />
                  </div>
                </div>
              )}
            </div>

            <Button
              onClick={fetchReportData}
              className="w-full gap-2"
              size="lg"
            >
              <BarChart3 className="w-4 h-4" />
              Generate Report
            </Button>
          </CardContent>
        </Card>

        {/* Report Content */}
        <div className="space-y-6 print:space-y-4">
          {/* Report Header */}
          <Card className="border-2 print:border-0 print:shadow-none">
            <CardContent className="pt-6">
              <div className="text-center space-y-2 border-b pb-6 mb-6">
                <h1 className="text-3xl font-bold text-primary print:text-black">
                  {restaurantName}
                </h1>
                {restaurantAddress && (
                  <p className="text-sm text-muted-foreground print:text-gray-600">
                    {restaurantAddress}
                  </p>
                )}
                <h2 className="text-xl font-semibold mt-4">INDENTS REPORT</h2>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground print:text-gray-600 flex-wrap">
                  <span>
                    Department:{" "}
                    <strong>
                      {selectedDepartment === "all"
                        ? "All Departments"
                        : selectedDepartment}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Status:{" "}
                    <strong>
                      {selectedStatus === "all" ? "All Status" : selectedStatus}
                    </strong>
                  </span>
                  <span>•</span>
                  <span>
                    Report Date:{" "}
                    <strong>
                      {new Date().toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </strong>
                  </span>
                  {dateFilter !== "all" && fromDate && toDate && (
                    <>
                      <span>•</span>
                      <span>
                        Period: <strong>{fromDate}</strong> to{" "}
                        <strong>{toDate}</strong>
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Summary Statistics */}
              <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6">
                <div className="bg-primary/5 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-muted-foreground print:text-gray-600">
                    Total Indents
                  </p>
                  <p className="text-2xl font-bold text-primary print:text-black">
                    {reportStats.total_indents || 0}
                  </p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-amber-700 print:text-gray-600">
                    Pending
                  </p>
                  <p className="text-2xl font-bold text-amber-600 print:text-black">
                    {reportStats.pending || 0}
                  </p>
                </div>
                <div className="bg-emerald-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-emerald-700 print:text-gray-600">
                    Approved
                  </p>
                  <p className="text-2xl font-bold text-emerald-600 print:text-black">
                    {reportStats.approved || 0}
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-blue-700 print:text-gray-600">
                    Converted
                  </p>
                  <p className="text-2xl font-bold text-blue-600 print:text-black">
                    {reportStats.converted || 0}
                  </p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-red-700 print:text-gray-600">
                    Rejected
                  </p>
                  <p className="text-2xl font-bold text-red-600 print:text-black">
                    {reportStats.rejected || 0}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-purple-700 print:text-gray-600">
                    Total Items
                  </p>
                  <p className="text-2xl font-bold text-purple-600 print:text-black">
                    {reportStats.total_items || 0}
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg text-center print:bg-gray-100">
                  <p className="text-sm text-orange-700 print:text-gray-600">
                    High Priority
                  </p>
                  <p className="text-2xl font-bold text-orange-600 print:text-black">
                    {reportStats.high_priority || 0}
                  </p>
                </div>
              </div>

              {/* Alert for Pending Items */}
              {reportStats.pending > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3 print:bg-gray-100 print:border-gray-300">
                  <AlertTriangle className="w-5 h-5 text-amber-600 print:text-black" />
                  <div>
                    <p className="font-medium text-amber-800 print:text-black">
                      Attention Required
                    </p>
                    <p className="text-sm text-amber-700 print:text-gray-600">
                      {reportStats.pending} indents are pending approval
                      {reportStats.high_priority > 0 &&
                        ` (${reportStats.high_priority} high priority)`}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Detailed Report Table */}
          <Card className="print:border-0 print:shadow-none">
            <CardHeader className="print:pb-2">
              <CardTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5" />
                Detailed Indents Report
              </CardTitle>
              <CardDescription className="print:text-gray-600">
                Showing {reportData.length} indent items
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border overflow-auto print:border-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 print:bg-gray-100">
                      <TableHead className="w-16 print:text-black">
                        S.No
                      </TableHead>
                      <TableHead className="print:text-black">
                        Indent No
                      </TableHead>
                      <TableHead className="print:text-black">
                        Department
                      </TableHead>
                      <TableHead className="print:text-black">
                        Item Name
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Requested Qty
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Approved Qty
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Issued Qty
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Unit
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Stock Available
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Need Purchase
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Priority
                      </TableHead>
                      <TableHead className="text-center print:text-black">
                        Status
                      </TableHead>
                      <TableHead className="print:text-black">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={13}
                          className="text-center py-12 text-muted-foreground"
                        >
                          No data available for the selected filters
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportData.map((indent, index) => (
                        <TableRow
                          key={`${indent.request_id}-${indent.ingredient_id}-${index}`}
                          className={
                            indent.status === 3
                              ? "bg-red-50/50 print:bg-gray-100"
                              : indent.status === 0
                              ? "bg-amber-50/50 print:bg-gray-50"
                              : indent.status === 1
                              ? "bg-emerald-50/50 print:bg-gray-50"
                              : ""
                          }
                        >
                          <TableCell className="font-medium print:text-black">
                            {index + 1}
                          </TableCell>
                          <TableCell className="font-medium text-primary print:text-black">
                            {indent.indent_no}
                          </TableCell>
                          <TableCell className="print:text-black">
                            {indent.department}
                          </TableCell>
                          <TableCell className="print:text-black">
                            {indent.ingredient_name || "N/A"}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {parseFloat(indent.requested_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {parseFloat(indent.approved_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {parseFloat(indent.issued_qty || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {indent.unit_name || "-"}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {parseFloat(indent.stock_available || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center print:text-black">
                            {indent.need_purchase ? "Yes" : "No"}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              className={cn(
                                "font-medium border-0 print:bg-white print:text-black print:border print:border-black",
                                priorityConfig[indent.priority?.toLowerCase()]
                                  ?.color || "bg-muted"
                              )}
                            >
                              {priorityConfig[indent.priority?.toLowerCase()]
                                ?.label || indent.priority}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              className={cn(
                                "font-medium border gap-1 print:bg-white print:text-black print:border print:border-black",
                                statusConfig[indent.status]?.color
                              )}
                            >
                              {statusConfig[indent.status]?.label || "Unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell className="print:text-black">
                            {new Date(indent.date).toLocaleDateString("en-IN")}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Report Footer */}
              <div className="mt-6 pt-6 border-t space-y-2 text-sm text-muted-foreground print:text-gray-600">
                <div className="flex justify-between">
                  <span>Report Generated On:</span>
                  <span className="font-medium">
                    {new Date().toLocaleString("en-IN", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Records:</span>
                  <span className="font-medium">{reportData.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Items Requested:</span>
                  <span className="font-medium text-lg">
                    {reportStats.total_items || 0}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:block,
          .print\\:block * {
            visibility: visible;
          }
          @page {
            margin: 1cm;
          }
        }
      `}</style>
    </MainLayout>
  );
}